import { useState, useEffect, useRef } from 'react'
import { Lock, BarChart3 } from 'lucide-react'
import { trackUpgradeCtaClicked, trackAnalyticsLockedView } from '@/shared/utils/analytics'
import UnlockDialog from './UnlockDialog'

interface AnalyticsLockedOverlayProps {
  children: React.ReactNode
}

/**
 * Overlay component that blurs analytics content for free users
 * Shows a lock icon and button to unlock analytics (opens the unlock dialog)
 */
export default function AnalyticsLockedOverlay({
  children,
}: AnalyticsLockedOverlayProps) {
  const [showUnlock, setShowUnlock] = useState(false)
  const trackedRef = useRef(false)

  // Track analytics_locked_view when overlay is shown
  useEffect(() => {
    if (!trackedRef.current) {
      trackedRef.current = true
      trackAnalyticsLockedView()
    }
  }, [])

  const handleUnlock = () => {
    trackUpgradeCtaClicked('analytics')
    setShowUnlock(true)
  }

  return (
    <div className="relative">
      {/* Blurred content */}
      <div className="filter blur-[3px] pointer-events-none select-none">
        {children}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/60 to-white/90 flex flex-col items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 text-center max-w-sm mx-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Analytics Locked
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Buy a test series to unlock detailed PYQ analytics, trends, and high-yield topic insights.
          </p>
          <button
            onClick={handleUnlock}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <BarChart3 className="w-4 h-4" />
            Unlock Analytics
          </button>
        </div>
      </div>

      <UnlockDialog
        isOpen={showUnlock}
        onClose={() => setShowUnlock(false)}
        title="Unlock detailed analytics"
        description="See high-yield units, subject analysis and trending topics from past NEET papers."
        source="analytics"
      />
    </div>
  )
}
